import React, { memo, useCallback, useEffect, useState } from 'react'
import { cn } from '@appfluence/classnames'
import styled from 'styled-components'
import { Label } from '../../text/Text'
import { ActionButton, CommandButton } from '@fluentui/react/lib/Button'
import { Spinner } from '@fluentui/react/lib/Spinner'
import { ContainerPanel } from '../../panels/ContainerPanel'
import { PanelView } from '../../panels/PanelView'
import { UsersList } from '../usersList/UsersList'
import { UsersViewWithSearch } from '../usersList/UsersListWithSearch'
import { UsersCollection } from '../usersCollection/UsersCollection'
import { createUserOptions } from '../../../helpers/usersOptionsHelpers'
import { withCyAttr } from '../../../helpers/styleHelper'
import { CYPRESS_ID } from '../../../constants/cypress'
import { ErrorText } from '../../text/ErrorText'
import { useTranslation } from 'react-i18next'

const EditionButton = styled(ActionButton).attrs(
  withCyAttr({
    styles: {
      label: {
        margin: 0,
      },
    },
  })
)`
  height: auto;
  padding: 0;
`

export const MenuButton = styled(CommandButton).attrs({
  iconProps: {
    iconName: 'CollapseMenu',
  },
  styles: {
    menuIcon: {
      marginRight: 0,
    },
  },
})`
  height: auto;
  padding: 0;
`

const SErrorText = styled(ErrorText)`
  padding: 2px 0;
`

const UsersHeaderView = memo(
  ({ header, users, editing, onRemoveUser, loading, disableOnLoading = false, fromProject = false, onShowError }) => {
    const { t } = useTranslation()
    if (users.length === 0) {
      return null
    }

    const usersOptions = createUserOptions(users, t)
    return (
      <>
        <Label>{header}</Label>
        {editing ? (
          <UsersList
            usersOptions={usersOptions}
            onSelectUser={disableOnLoading && loading ? undefined : onRemoveUser}
            editing={editing}
            fromProject={fromProject}
            onShowError={onShowError}
          />
        ) : (
          <UsersCollection
            users={users}
            max={11}
            showAddCell={false}
            onClickUser={onRemoveUser}
            tooltipText={t('users.remove_user')}
          />
        )}
      </>
    )
  }
)

const HeaderContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  min-height: 20px;
  padding-bottom: 4px;

  &.spaceBetween {
    justify-content: space-between;
  }

  &.flexStart {
    justify-content: flex-start;
  }
`

const UsersPanelView = memo(
  ({
    users,
    userGroups,
    onSelectGroup,
    options,
    onSelectUser,
    onRemoveUser,
    loading,
    disableOnLoading = false,
    enabledEditing,
    onStartEditing,
    onEndEditing,
    editingErrorMessage,
    errorMessage,
    usersHeader,
    textFieldHeader,
    textFieldPlaceholder,
    invitationIconProps,
    doneText,
    editText,
    menuProps,
    fromProject,
    onShowError,
    ...rest
  }) => {
    const [editing, setEditing] = useState(false)
    const { t } = useTranslation()

    // THIS IS A HACK TO ENSURE THIS PANEL IS DISPLAY OVER OTHER DISPLAYS
    useEffect(() => {
      const element = document.getElementById('fluent-default-layer-host')
      element.style.zIndex = '1000001'
      return () => {
        element.style.zIndex = '1000000'
      }
    }, [])

    const toggleEditing = useCallback(() => {
      if (editing) {
        onEndEditing && onEndEditing()
      } else {
        onStartEditing && onStartEditing()
      }
      setEditing(!editing)
    }, [editing, onEndEditing, onStartEditing])

    const showEditionButtons = !loading && users.length !== 0
    const headerContainerClassName = cn({ spaceBetween: showEditionButtons && !editing }, { flexStart: loading })
    const headerChildren = (
      <HeaderContainer className={headerContainerClassName}>
        {loading && <Spinner label={t('general.loading')} labelPosition="right" />}
        {showEditionButtons && (
          <EditionButton cy={CYPRESS_ID.USER_PANEL_EDIT} onClick={toggleEditing}>
            {editing ? doneText : editText}
          </EditionButton>
        )}
        {!loading && !editing && menuProps && <MenuButton menuProps={menuProps} />}
      </HeaderContainer>
    )

    return (
      <PanelView headerChildren={headerChildren} {...rest}>
        {editing && editingErrorMessage && <SErrorText>{editingErrorMessage}</SErrorText>}
        {errorMessage && <SErrorText>{errorMessage}</SErrorText>}
        <UsersHeaderView
          header={usersHeader}
          users={users}
          editing={editing}
          onRemoveUser={onRemoveUser}
          loading={loading}
          fromProject={fromProject}
          onShowError={onShowError}
        />
        {!editing && (
          <UsersViewWithSearch
            textFieldHeader={textFieldHeader}
            textFieldPlaceholder={textFieldPlaceholder ?? t('item_detail.followers_panel_text_field_placeholder')}
            invitationIconProps={invitationIconProps}
            selectedUsers={users}
            options={options}
            onSelectUser={disableOnLoading && loading ? undefined : onSelectUser}
            userGroups={userGroups}
            onSelectGroup={onSelectGroup}
          />
        )}
      </PanelView>
    )
  }
)

export const UsersPanel = memo(
  ({
    children,
    headerText,
    users,
    userGroups,
    onSelectGroup,
    options,
    onSelectUser,
    onRemoveUser,
    loading,
    disableOnLoading = false,
    enabledEditing = true,
    onStartEditing,
    onEndEditing,
    editingErrorMessage,
    errorMessage,
    usersHeader,
    textFieldHeader,
    textFieldPlaceholder,
    invitationIconProps,
    doneText,
    editText,
    menuProps,
    onDismiss,
    fromProject = false,
    onShowError,
    ...rest
  }) => {
    const { t } = useTranslation()
    return (
      <ContainerPanel headerText={headerText ?? t('users.choose_users')} onDismiss={onDismiss} {...rest}>
        <UsersPanelView
          users={users}
          userGroups={userGroups}
          onSelectGroup={onSelectGroup}
          options={options}
          onSelectUser={onSelectUser}
          onRemoveUser={onRemoveUser}
          loading={loading}
          disableOnLoading={disableOnLoading}
          enabledEditing={enabledEditing}
          onStartEditing={onStartEditing}
          onEndEditing={onEndEditing}
          editingErrorMessage={editingErrorMessage}
          errorMessage={errorMessage}
          usersHeader={usersHeader ?? 'Remove users'}
          textFieldHeader={textFieldHeader}
          textFieldPlaceholder={textFieldPlaceholder}
          invitationIconProps={invitationIconProps}
          doneText={doneText ?? t('users.done')}
          editText={editText ?? t('users.edit')}
          menuProps={menuProps}
          fromProject={fromProject}
          onShowError={onShowError}
        />
        {children}
      </ContainerPanel>
    )
  }
)
